import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authentication } from '@perpay-web/authentication/singleton';
import { PHO_MANUAL_PAYMENT_CS_NUMBER_FEATURE_SWITCH } from '@perpay-web/constants/featureSwitches';
import {
    analyticsCardManualSetupMVP2ClickForm,
    analyticsCardManualSetupMVP2ClickInstructions,
    analyticsCardManualSetupMVP2ClickDone,
    analyticsCardManualSetupMVP2ClickTryAgain,
    analyticsCardManualSetupMVP2PageView,
} from '@perpay-web/fintech/actions/analytics/cardOnboardingMVP2';
import CardManualPaymentSetup from '@perpay-web/fintech/components/composite/CardManualPaymentSetup/CardManualPaymentSetup';
import { CREDIT_KARMA_LIGHTBOX } from '@perpay-web/fintech/constants/partnerOnboardingCodes';
import {
    cardManualPaymentSetupDataModule,
    getAccountNumber,
    getRoutingNumber,
    getMinimumPayment,
    getDirectDepositFormUrl,
} from '@perpay-web/fintech/dataModules/composed/cardManualPaymentSetup';
import { getIsPinwheelEligible } from '@perpay-web/fintech/dataModules/composed/cardPaymentSummary';
import { fetchFeatureSwitchesDataModule } from '@perpay-web/fintech/dataModules/fetchFeatureSwitches';
import { getFeatureSwitchEnabled } from '@perpay-web/fintech/dataModules/fetchFeatureSwitches';
import { fetchPayrollInstructionsDataModule } from '@perpay-web/fintech/dataModules/fetchPayrollInstructions';
import { getAmountToCharge } from '@perpay-web/fintech/selectors/ui/cardOnboardingMVP2';
import { getSignupFlowType } from '@perpay-web/fintech/selectors/ui/signup';

/**
 * Container component for CardManualPaymentSetup MVP2
 * @param {Object} props - Component props
 * @param {Function} [props.goBackToPinwheel] - Callback to go back to Pinwheel
 * @returns {JSX.Element} CardManualPaymentSetup component
 */
const CardManualPaymentSetupContainer = ({
    goBackToPinwheel,
    isPHO = false,
}) => {
    const dispatch = useDispatch();

    const isLoading = useSelector(
        cardManualPaymentSetupDataModule.getIsLoadingOrUnrequested,
    );
    const minimumPayment = useSelector(getMinimumPayment);
    const accountNumber = useSelector(getAccountNumber);
    const routingNumber = useSelector(getRoutingNumber);
    const directDepositFormUrl = useSelector(getDirectDepositFormUrl);
    const isPinwheelEligible = useSelector(getIsPinwheelEligible);
    const amountToCharge = useSelector(getAmountToCharge);
    const signupFlow = useSelector(getSignupFlowType);
    const isCustomerServiceInfoEnabled = useSelector((state) =>
        getFeatureSwitchEnabled(
            state,
            PHO_MANUAL_PAYMENT_CS_NUMBER_FEATURE_SWITCH.storeName,
        ),
    );

    const partnerCode = authentication.getPartnerOnboardedCode();
    const showCustomerServiceInfo =
        isCustomerServiceInfoEnabled && partnerCode === CREDIT_KARMA_LIGHTBOX;

    const fetchPaymentSetupData = useCallback(
        (amount) => {
            dispatch(
                cardManualPaymentSetupDataModule.dataRequest({
                    actions: [
                        fetchPayrollInstructionsDataModule.dataRequest({
                            amount,
                        }),
                    ],
                }),
            );
        },
        [dispatch],
    );

    const onMount = useCallback(() => {
        fetchPaymentSetupData(amountToCharge);
        dispatch(
            fetchFeatureSwitchesDataModule.dataRequest(
                PHO_MANUAL_PAYMENT_CS_NUMBER_FEATURE_SWITCH,
            ),
        );
        dispatch(analyticsCardManualSetupMVP2PageView(signupFlow));
    }, [dispatch, fetchPaymentSetupData, amountToCharge, signupFlow]);

    const onModalClose = useCallback(() => {
        // TODO make better
        // we need this function to re-fetch the payroll instructions since when
        // the modal is closed, the fetchPayrollInstructionsDataModule is reset
        // otherwise there's an infinite loader situation
        fetchPaymentSetupData(amountToCharge);
        dispatch(
            fetchFeatureSwitchesDataModule.dataRequest(
                PHO_MANUAL_PAYMENT_CS_NUMBER_FEATURE_SWITCH,
            ),
        );
        dispatch(analyticsCardManualSetupMVP2PageView(signupFlow));
    }, [dispatch, fetchPaymentSetupData, amountToCharge, signupFlow]);

    const onClickTryAgain = useCallback(() => {
        goBackToPinwheel();
        dispatch(analyticsCardManualSetupMVP2ClickTryAgain(signupFlow));
    }, [dispatch, goBackToPinwheel, signupFlow]);

    const onClickDone = useCallback(
        () => dispatch(analyticsCardManualSetupMVP2ClickDone(signupFlow)),
        [dispatch, signupFlow],
    );

    const onClickForm = useCallback(
        () => dispatch(analyticsCardManualSetupMVP2ClickForm(signupFlow)),
        [dispatch, signupFlow],
    );

    const onClickInstructions = useCallback(
        () =>
            dispatch(analyticsCardManualSetupMVP2ClickInstructions(signupFlow)),
        [dispatch, signupFlow],
    );

    const onUnmount = useCallback(() => {
        dispatch(cardManualPaymentSetupDataModule.dataReset());
    }, [dispatch]);

    return (
        <CardManualPaymentSetup
            isLoading={isLoading}
            minimumPayment={minimumPayment}
            amountToCharge={amountToCharge}
            accountNumber={accountNumber}
            routingNumber={routingNumber}
            directDepositFormUrl={directDepositFormUrl}
            isPinwheelEligible={isPinwheelEligible}
            signupFlow={signupFlow}
            showCustomerServiceInfo={showCustomerServiceInfo}
            onMount={onMount}
            onModalClose={onModalClose}
            onClickTryAgain={onClickTryAgain}
            onClickDone={onClickDone}
            onClickForm={onClickForm}
            onClickInstructions={onClickInstructions}
            onUnmount={onUnmount}
            isPHO={isPHO}
        />
    );
};

export default CardManualPaymentSetupContainer;
